<template>
  <section id="process" class="collaboration" :class="{ 'is-light': theme === 'light', 'contact-open': contactOpen }" @pointermove="followPointer" @pointerleave="resetTilt" @keydown.esc="closeContact">
    <div class="curved-grid" aria-hidden="true">
      <svg viewBox="0 0 1440 720" preserveAspectRatio="xMidYMid slice" focusable="false">
        <path v-for="(path, index) in gridPaths" :key="index" :d="path" vector-effect="non-scaling-stroke" />
      </svg>
    </div>
    <div class="collaboration-inner">
      <div class="collaboration-intro">
        <div class="brand-perspective" aria-hidden="true">
          <div ref="brandCard" class="brand-card" :style="{ transform: `rotateX(${tiltX}deg) rotateY(${tiltY}deg)` }">
            <img src="/纪煜科技-透明.png" alt="" draggable="false">
          </div>
        </div>
        <h2>{{ copy.title }}</h2>
        <p class="collaboration-summary">{{ copy.summary }}</p>
        <button ref="contactTrigger" class="project-contact-button" type="button" :aria-expanded="contactOpen" aria-controls="project-contact-panel" @click="toggleContact">
          <span>{{ copy.cta }}</span><span class="button-arrow material-symbols-rounded" aria-hidden="true">arrow_forward</span>
        </button>
      </div>
      <p class="collaboration-note">{{ copy.note }}</p>
      <Transition name="project-contact">
        <div v-show="contactOpen" id="project-contact-panel" ref="contactPanel" class="project-contact-panel" role="region" :aria-label="copy.contactTitle" tabindex="-1" :inert="!contactOpen">
          <div class="project-contact-clip">
            <div class="project-contact-content">
              <button class="contact-close" type="button" :aria-label="copy.close" @click="closeContact"><span class="material-symbols-rounded" aria-hidden="true">close</span></button>
              <div class="contact-switcher" role="group" :aria-label="copy.chooseContact">
                <button v-for="person in contacts" :key="person.id" type="button" :aria-pressed="selectedContact === person.id" @click="selectedContact = person.id">{{ person.name }}</button>
              </div>
              <figure class="wechat-contact">
                <img :src="activeContact.qr" :alt="`${activeContact.name} · ${copy.qrAlt}`" width="176" height="176">
                <figcaption>{{ copy.scan }}</figcaption>
              </figure>
              <div class="project-contact-details">
                <h3>{{ activeContact.name }}<span class="contact-role">{{ activeContact.role }}</span></h3>
                <p>{{ copy.contactHint }}</p>
                <a :href="`tel:${activeContact.phone}`"><span class="material-symbols-rounded" aria-hidden="true">call</span><span><small>{{ copy.phone }}</small>{{ activeContact.phoneDisplay }}</span></a>
                <a href="mailto:nbjy@nbjiyu-tech.cn"><span class="material-symbols-rounded" aria-hidden="true">mail</span><span><small>{{ copy.email }}</small>nbjy@nbjiyu-tech.cn</span></a>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>

<script>
import zhouQr from '../assets/jiehui.png'
import tangQr from '../assets/changqing.jpg'

// Both line families share one projection, forming a continuous curved mesh.
function gridLine(fixed, vertical) {
  return Array.from({ length: 81 }, (_, index) => {
    const moving = index / 40 - 1
    const u = vertical ? fixed : moving
    const v = vertical ? moving : fixed
    const x = 720 + u * (1 - .2 * (1 - v * v)) * 710
    const y = 360 + v * (1 - .25 * (1 - u * u)) * 350
    return `${index ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`
  }).join(' ')
}
const gridPaths = [
  ...Array.from({ length: 25 }, (_, i) => gridLine(i / 12 - 1, true)),
  ...Array.from({ length: 17 }, (_, i) => gridLine(i / 8 - 1, false))
]

export default {
  name: 'CollaborationSection',
  props: { language: { type: String, default: 'zh' }, theme: { type: String, default: 'dark' } },
  data: () => ({ tiltX: 0, tiltY: 0, gridPaths, contactOpen: false, selectedContact: 'tang' }),
  computed: {
    contacts() {
      const zh = this.language === 'zh'
      return [
        { id: 'tang', name: zh ? '唐经理' : 'Mr. Tang', role: zh ? '技术经理' : 'Technical Manager', qr: tangQr, phone: '15657844886', phoneDisplay: '156 5784 4886' },
        { id: 'zhou', name: zh ? '周经理' : 'Mr. Zhou', role: zh ? '销售经理' : 'Sales Manager', qr: zhouQr, phone: '17605702079', phoneDisplay: '176 0570 2079' }
      ]
    },
    activeContact() { return this.contacts.find(person => person.id === this.selectedContact) || this.contacts[0] },
    copy() { return this.language === 'zh'
      ? { title: '下一个产品，从你的想法开始。', summary: '不需要准备完整方案，先聊聊你的业务，以及最想解决的问题。', cta: '聊聊你的项目', note: '直接对接技术负责人，分阶段确认需求与成果。', contactTitle: '聊聊你的项目', chooseContact: '选择联系人', contactHint: '微信、电话或邮件，选择你方便的方式。', scan: '微信扫码，聊聊你的想法', qrAlt: '微信联系二维码', phone: '电话', email: '邮箱', close: '收起联系方式' }
      : { title: 'Your next product starts with your idea.', summary: 'No complete brief needed. Tell us about your business and the problem you want to solve.', cta: 'Let’s talk about your project', note: 'Work directly with the technical lead. Review requirements and results at each stage.', contactTitle: 'Let’s talk about your project', chooseContact: 'Choose a contact', contactHint: 'Reach us on WeChat, by phone, or by email.', scan: 'Scan with WeChat to start a conversation', qrAlt: 'WeChat contact QR code', phone: 'Phone', email: 'Email', close: 'Hide contact details' } }
  },
  methods: {
    toggleContact() {
      if (this.contactOpen) { this.closeContact(); return }
      this.contactOpen = true
    },
    closeContact(event) {
      if (!this.contactOpen) return
      this.contactOpen = false
      if (event?.type === 'keydown') this.$refs.contactTrigger?.focus({ preventScroll: true })
    },
    followPointer(event) {
      if (event.pointerType === 'touch' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      const rect = this.$refs.brandCard.parentElement.getBoundingClientRect()
      this.tiltY = Math.max(-28, Math.min(28, (event.clientX - rect.left - rect.width / 2) / 16))
      this.tiltX = Math.max(-22, Math.min(22, -(event.clientY - rect.top - rect.height / 2) / 16))
    },
    resetTilt() { this.tiltX = 0; this.tiltY = 0 }
  }
}
</script>

<style scoped>
/* On desktop this section and the compact footer share one viewport below navigation. */
.collaboration{position:relative;isolation:isolate;overflow:hidden;min-height:680px;padding:38px 24px;display:grid;place-items:center;text-align:center;background:var(--page);color:#f7f8fa;transition:background-color .7s,color .7s;scroll-margin-top:64px;overflow-anchor:none}
.curved-grid{position:absolute;inset:0;z-index:-1;pointer-events:none;color:rgba(188,201,217,.22);transition:color .9s;mask-image:radial-gradient(ellipse at center,transparent 13%,#000 48%,transparent 74%)}
.curved-grid svg{display:block;width:100%;height:100%;fill:none;stroke:currentColor;stroke-width:.65}
.is-light .curved-grid{color:rgba(40,57,76,.22)}
.collaboration-inner{position:relative;width:min(100%,920px);height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center}
.collaboration-intro{width:100%;transition:transform .42s cubic-bezier(.22,1,.36,1)}
.brand-perspective{perspective:800px;width:144px;height:144px;margin:0 auto 38px}
.brand-card{width:100%;height:100%;display:grid;place-items:center;overflow:hidden;border:1px solid rgba(255,255,255,.2);border-radius:32px;background:#070809;box-shadow:0 18px 45px rgba(0,0,0,.35),inset 0 1px 1px rgba(255,255,255,.1);transition:transform .45s cubic-bezier(.22,1,.36,1),background-color .7s,border-color .7s,box-shadow .7s;will-change:transform}
.brand-card img{width:118px;height:106px;object-fit:cover;object-position:left;filter:grayscale(1) brightness(0) invert(1);user-select:none;pointer-events:none;transition:filter .7s}
h2{font-size:clamp(28px,3.3vw,46px);line-height:1.3;font-weight:500;letter-spacing:-.035em}
.collaboration-summary{margin:18px auto 0;max-width:600px;font-size:14px;line-height:1.8;color:#9299a1;transition:color .7s}
.project-contact-button{display:inline-flex;align-items:center;justify-content:center;min-height:48px;max-width:100%;margin-top:28px;padding:12px 25px;border:1px solid var(--line-strong);border-radius:999px;background:transparent;color:inherit;font:inherit;font-size:14px;cursor:pointer;transition:color .25s,border-color .25s}
.project-contact-button:hover,.project-contact-button:focus-visible{border-color:currentColor}
.button-arrow{display:block;flex-shrink:0;width:0;margin-left:0;overflow:hidden;opacity:0;transform:translateX(-6px);font-size:20px;transition:width .35s cubic-bezier(.22,1,.36,1),margin-left .35s cubic-bezier(.22,1,.36,1),opacity .25s,transform .35s cubic-bezier(.22,1,.36,1)}
.project-contact-button:focus-visible .button-arrow{width:20px;margin-left:12px;opacity:1;transform:translateX(0)}
@media(hover:hover) and (pointer:fine){.project-contact-button:hover .button-arrow{width:20px;margin-left:12px;opacity:1;transform:translateX(0)}}
.collaboration-note{margin:14px auto 0;max-width:580px;font-size:12px;line-height:1.8;color:var(--muted)}
.project-contact-panel{position:absolute;z-index:5;top:56%;left:50%;display:block;width:min(100%,620px);margin:0;text-align:left;outline:none;overflow-anchor:none;transform:translate(-50%,-50%)}
/* An unpadded grid child can collapse completely, without a final padding-height jump. */
.project-contact-clip{min-height:0;overflow:hidden}
.project-contact-content{position:relative;min-height:0;overflow:hidden;display:grid;grid-template-columns:176px minmax(0,1fr);align-items:center;gap:28px;padding:28px;border:1px solid var(--line-strong);border-radius:20px;background:var(--page);transition:background-color .7s,border-color .7s}
.contact-switcher{grid-column:1/-1;justify-self:center;display:flex;gap:4px;padding:4px;border:1px solid var(--line);border-radius:999px}
.contact-switcher button{min-height:36px;padding:7px 20px;border:0;border-radius:999px;background:transparent;color:var(--muted);font-size:13px;cursor:pointer;transition:background-color .2s,color .2s}
.contact-switcher button:hover{color:var(--ink)}
.contact-switcher button[aria-pressed="true"]{background:var(--ink);color:var(--page)}
.contact-role{display:inline-block;margin-left:10px;color:var(--muted);font-size:12px;font-weight:400}
.contact-close{position:absolute;top:6px;right:6px;width:36px;height:36px;display:grid;place-items:center;border:0;border-radius:50%;background:transparent;color:var(--muted);cursor:pointer}
.contact-close:hover{color:var(--ink)}
.contact-close .material-symbols-rounded{font-size:20px}
.wechat-contact{margin:0;text-align:center}
.wechat-contact img{display:block;width:176px;height:176px;max-width:100%;padding:10px;object-fit:contain;background:#fff;border-radius:10px}
.wechat-contact figcaption{margin-top:10px;color:var(--muted);font-size:11px;line-height:1.6}
.project-contact-details h3{padding-right:10px;font-size:17px;font-weight:500;line-height:1.5}
.project-contact-details>p{margin-top:8px;color:var(--muted);font-size:12px;line-height:1.7}
.project-contact-details a{display:flex;align-items:center;gap:12px;margin-top:16px;color:inherit;text-decoration:none;font-size:14px;overflow-wrap:anywhere}
.project-contact-details a:hover{text-decoration:underline;text-underline-offset:4px}
.project-contact-details small{display:block;margin-bottom:3px;color:var(--muted);font-size:10px}
.project-contact-details .material-symbols-rounded{flex-shrink:0;font-size:18px;color:var(--muted)}
.project-contact-button:focus-visible,.contact-close:focus-visible,.contact-switcher button:focus-visible,.project-contact-details a:focus-visible,.project-contact-panel:focus-visible{outline:2px solid var(--accent);outline-offset:3px;border-radius:12px}
.project-contact-panel:focus-visible{outline:none}
.project-contact-button:focus-visible{border-radius:999px}
.collaboration-note{transition:transform .42s cubic-bezier(.22,1,.36,1)}
.contact-open .collaboration-intro{transform:translateY(-185px)}
.contact-open .collaboration-note{transform:translateY(120px)}
.project-contact-enter-active,.project-contact-leave-active{transition:opacity .2s ease,transform .24s cubic-bezier(.22,1,.36,1)}
.project-contact-enter-from,.project-contact-leave-to{opacity:0;transform:translate(-50%,calc(-50% + 8px)) scale(.98)}
.is-light{background:var(--page);color:#111417}
.is-light .brand-card{background:#f7f8f8;border-color:rgba(12,17,22,.17);box-shadow:0 18px 45px rgba(28,39,49,.1),inset 0 1px 1px #fff}
.is-light .brand-card img{filter:grayscale(1) brightness(0)}
.is-light .collaboration-summary{color:#5d656d}
@media(max-width:620px){.collaboration{height:calc(100svh - 64px);min-height:680px;padding:32px 20px;scroll-margin-top:64px}.brand-perspective{width:124px;height:124px;margin-bottom:32px}.brand-card{border-radius:28px}.brand-card img{width:104px;height:94px}.collaboration-summary{font-size:13px;max-width:300px}.curved-grid{inset:0 -30%}}
@media(max-width:620px){.project-contact-content{grid-template-columns:1fr;gap:20px;padding:36px 22px 28px}.wechat-contact{justify-self:center}.project-contact-details{width:100%}.project-contact-details h3,.project-contact-details>p{text-align:center}.collaboration-note{max-width:300px;font-size:11px}}
@media(prefers-reduced-motion:reduce){.brand-card{transform:none!important;will-change:auto}.brand-card{transition:none}}
@media(prefers-reduced-motion:reduce){.project-contact-button,.button-arrow,.contact-switcher button,.project-contact-enter-active,.project-contact-leave-active{transition:none}.button-arrow,.project-contact-enter-from,.project-contact-leave-to{transform:none}}
@media(min-width:981px){
  .collaboration{height:calc(100svh - 244px);min-height:620px;padding:38px 24px}
  .brand-perspective{width:92px;height:92px;margin-bottom:24px}
  .brand-card{border-radius:21px}
  .brand-card img{width:76px;height:68px}
  h2{font-size:clamp(24px,2.64vw,37px)}
  .collaboration-summary{margin-top:14px;max-width:500px;font-size:12px}
  .project-contact-button{min-height:38px;margin-top:22px;padding:9px 20px;font-size:12px}
  .button-arrow{font-size:16px}
  .collaboration-note{margin-top:11px;font-size:10px}
  .project-contact-panel{top:63%;width:min(100%,440px)}
  .project-contact-content{grid-template-columns:120px minmax(0,1fr);gap:18px;padding:18px;border-radius:14px}
  .contact-switcher{padding:3px}
  .contact-switcher button{min-height:26px;padding:5px 14px;font-size:10px}
  .contact-close{width:26px;height:26px}
  .contact-close .material-symbols-rounded{font-size:14px}
  .wechat-contact img{width:120px;height:120px;padding:7px}
  .wechat-contact figcaption{margin-top:6px;font-size:8px}
  .project-contact-details h3{font-size:13px}
  .contact-role,.project-contact-details>p{font-size:9px}
  .project-contact-details a{gap:8px;margin-top:11px;font-size:11px}
  .project-contact-details small{font-size:8px}
  .project-contact-details .material-symbols-rounded{font-size:14px}
}
@media(max-width:620px){
  h2{font-size:24px}
  .brand-perspective{width:108px;height:108px}
  .brand-card img{width:88px;height:80px}
  .collaboration-summary{font-size:12px}
  .project-contact-button{min-height:44px;padding:10px 22px;font-size:12px}
  .button-arrow{font-size:16px}
  .contact-open .collaboration-intro{transform:translateY(-184px)}
  .contact-open .collaboration-note{transform:translateY(100px)}
  .project-contact-panel{top:57%;width:calc(100% - 8px);max-height:none;overflow:visible}
  .project-contact-content{grid-template-columns:116px minmax(0,1fr);gap:16px;padding:18px 16px}
  .wechat-contact img{width:116px;height:116px}
  .project-contact-details h3,.project-contact-details>p{text-align:left}
  .contact-switcher{grid-column:1/-1}
}
</style>
